import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Flag, X } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REPORT_REASONS = [
    { id: 'spam', label: 'Spam', description: 'Unwanted promotional content' },
    { id: 'harassment', label: 'Harassment', description: 'Bullying or threatening behavior' },
    { id: 'nsfw', label: 'NSFW Content', description: 'Inappropriate or adult content' },
    { id: 'violence', label: 'Violence', description: 'Violent or dangerous content' },
    { id: 'hate', label: 'Hate Speech', description: 'Discrimination or hate content' },
    { id: 'other', label: 'Other', description: 'Other violation' },
];

const ReportModal = ({
    isOpen,
    onClose,
    contentType,  // 'message', 'popup', 'user'
    contentId,
    contentPreview,
    popupId,
    targetUserId,
    targetUsername
}) => {
    const [selectedReason, setSelectedReason] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        if (!selectedReason) {
            toast.error('Please select a reason');
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('chatAppToken');
            await axios.post(`${API}/reports`, {
                content_type: contentType,
                content_id: contentId,
                popup_id: popupId,
                target_user_id: targetUserId,
                target_username: targetUsername,
                content_preview: contentPreview,
                reason: selectedReason,
                description: description.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Report submitted. Thank you for helping keep our community safe.');
            onClose();
            setSelectedReason('');
            setDescription('');
        } catch (error) {
            console.error('Error submitting report:', error);
            if (error.response?.status === 429) {
                toast.error('Report limit reached. Please try again later.');
            } else {
                toast.error('Failed to submit report. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent
                className="max-w-md"
                style={{ backgroundColor: '#260e10', borderColor: 'rgba(255, 200, 87, 0.2)' }}
            >
                <DialogHeader>
                    <DialogTitle className="text-[#FFC857] flex items-center gap-2">
                        <Flag className="w-5 h-5 text-red-500" />
                        Report {contentType === 'message' ? 'Message' : contentType === 'popup' ? 'Popup' : 'User'}
                    </DialogTitle>
                    <DialogDescription className="text-[#F5E3D7]/70">
                        Help us understand what's wrong with this content.
                    </DialogDescription>
                </DialogHeader>

                {/* Content Preview */}
                {contentPreview && (
                    <div
                        className="p-3 rounded-lg text-sm"
                        style={{ backgroundColor: '#1a0a0e' }}
                    >
                        <p className="text-[#F5E3D7]/60 text-xs mb-1">Content being reported:</p>
                        <p className="text-[#F5E3D7] line-clamp-2">{contentPreview}</p>
                        {targetUsername && (
                            <p className="text-[#F5E3D7]/50 text-xs mt-1">By: @{targetUsername}</p>
                        )}
                    </div>
                )}

                {/* Reason Selection */}
                <div className="space-y-2">
                    <p className="text-sm font-medium text-[#F5E3D7]">Select a reason *</p>
                    <div className="grid grid-cols-2 gap-2">
                        {REPORT_REASONS.map(reason => (
                            <button
                                key={reason.id}
                                onClick={() => setSelectedReason(reason.id)}
                                className={`p-3 rounded-lg text-left transition-all ${selectedReason === reason.id
                                        ? 'bg-red-600/20 border-red-500'
                                        : 'bg-[#1a0a0e] border-transparent hover:bg-[#3d1a24]'
                                    } border`}
                            >
                                <p className={`text-sm font-medium ${selectedReason === reason.id ? 'text-red-400' : 'text-[#F5E3D7]'
                                    }`}>
                                    {reason.label}
                                </p>
                                <p className="text-xs text-[#F5E3D7]/50 mt-0.5">{reason.description}</p>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Optional Details */}
                <div className="space-y-2">
                    <p className="text-sm font-medium text-[#F5E3D7]">Additional details (optional)</p>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Provide any additional context..."
                        className="w-full h-20 p-3 rounded-lg text-sm resize-none border-0 focus:ring-1 focus:ring-[#FFC857]"
                        style={{ backgroundColor: '#1a0a0e', color: '#F5E3D7' }}
                        maxLength={500}
                    />
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="border-[#3d1a24] text-[#F5E3D7] hover:bg-[#3d1a24]"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!selectedReason || loading}
                        className="bg-red-600 hover:bg-red-700"
                    >
                        {loading ? 'Submitting...' : 'Submit Report'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ReportModal;
